import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {HTMLAnchorElement} [productTitleLink] - The product title link element.
 * @property {HTMLElement} [productBrand] - The product brand element.
 * @property {HTMLElement} [productCategoryLabel] - The product category label element.
 * @property {HTMLElement} [productPrice] - The product price container.
 * @property {HTMLScriptElement} [stitcherData] - The stitcher product payload.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];
  /** @type {Record<string, { brand?: string, title?: string, categoryLabel?: string, url?: string, priceHtml?: string, imageUrl?: string, hoverImageUrl?: string, featuredMediaUrl?: string, imageAlt?: string, quickAdd?: { productId?: string, sizeLabel?: string, selectedVariantId?: string, sizes?: Array<{ id?: string, label?: string, available?: boolean, url?: string, imageUrl?: string }> } }> | null} */
  #stitcherProducts = null;

  /** @type {HTMLElement | null} */
  #inlineQuickAddPanel = null;

  /** @type {Comment | null} */
  #inlineQuickAddPlaceholder = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.#handleStitcherClick);
    this.addEventListener('click', this.handleInlineQuickAddClick);
    document.addEventListener('click', this.#handleDocumentClick);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    //this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleStitcherClick);
    this.removeEventListener('click', this.handleInlineQuickAddClick);
    document.removeEventListener('click', this.#handleDocumentClick);
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    this.removeEventListener('click', this.navigateToProduct);

    const quickAddPanel = this.#inlineQuickAddPanel;
    if (quickAddPanel && quickAddPanel.parentElement === document.body) {
      quickAddPanel.remove();
      document.body.classList.remove('quickadd_modal_open_new');
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    // Close the inline quick add when crossing the mobile/desktop breakpoint,
    // since the panel is portaled to the body only for the mobile layout.
    this.#closeInlineQuickAdd();

    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * Updates card content when a stitcher swatch is selected.
   * @param {MouseEvent} event
   */
  #handleStitcherClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const stitcherButton = event.target.closest('[data-stitcher-button]');
    const quickAddPanel = this.#getInlineQuickAddPanel();
    if (!(stitcherButton instanceof HTMLButtonElement)) return;
    if (!this.contains(stitcherButton) && quickAddPanel?.contains(stitcherButton) !== true) return;

    const productId = stitcherButton.dataset.productId;
    if (!productId) return;

    const stitcherProduct = this.#getStitcherProducts()?.[productId];
    if (!stitcherProduct) return;

    // Stitcher selected inside the quick add panel only updates the panel,
    // never the card content or its swatches.
    if (stitcherButton.closest('.card_inline_quickadd_stitchers_new')) {
      if (stitcherProduct.quickAdd) {
        this.#renderInlineQuickAdd(stitcherProduct.quickAdd);
      }
      this.#updateActiveStitcher(stitcherButton, { panelOnly: true });
      return;
    }

    this.#applyStitcherProduct(stitcherProduct);
    this.#updateActiveStitcher(stitcherButton);
  };

  /**
   * Parses the stitcher payload rendered in Liquid.
   * @returns {Record<string, { brand?: string, title?: string, categoryLabel?: string, url?: string, priceHtml?: string, imageUrl?: string, hoverImageUrl?: string, featuredMediaUrl?: string, imageAlt?: string, quickAdd?: { productId?: string, brand?: string, title?: string, priceHtml?: string, summaryImageUrl?: string, displayLabel?: string, sizeLabel?: string, selectedVariantId?: string, sizes?: Array<{ id?: string, label?: string, available?: boolean, url?: string, imageUrl?: string, displayLabel?: string }> } }> | null}
   */
  #getStitcherProducts() {
    if (this.#stitcherProducts !== null) return this.#stitcherProducts;

    const stitcherData = this.refs.stitcherData;
    if (!(stitcherData instanceof HTMLScriptElement) || !stitcherData.textContent?.trim()) {
      this.#stitcherProducts = {};
      return this.#stitcherProducts;
    }

    try {
      this.#stitcherProducts = JSON.parse(stitcherData.textContent);
    } catch (error) {
      console.error('Failed to parse stitcher product data', error);
      this.#stitcherProducts = {};
    }

    return this.#stitcherProducts;
  }

  /**
   * Handles inline quick add interactions.
   * @param {MouseEvent} event
   */
  handleInlineQuickAddClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const quickAddPanel = this.#getInlineQuickAddPanel();
    /** @param {Element | null} element */
    const ownsElement = (element) =>
      element != null && (this.contains(element) || quickAddPanel?.contains(element) === true);

    const quickViewButton = event.target.closest('.card_quickview_new');
    if (quickViewButton && this.contains(quickViewButton)) {
      event.preventDefault();
      const panel = this.#getInlineQuickAddPanel();
      if (panel?.hasAttribute('data-single-variant')) {
        this.#directAddToCart();
        return;
      }
      this.#toggleInlineQuickAdd();
      return;
    }

    const sizeButton = event.target.closest('[data-quick-size-option]');
    if (sizeButton instanceof HTMLButtonElement && ownsElement(sizeButton)) {
      event.preventDefault();
      this.#selectInlineQuickAddSize(sizeButton);
      return;
    }

    const navButton = event.target.closest('[data-quick-size-nav]');
    if (navButton instanceof HTMLButtonElement && ownsElement(navButton)) {
      event.preventDefault();
      this.#scrollInlineQuickAddSizes(navButton.dataset.quickSizeNav === 'next' ? 1 : -1);
      return;
    }

    const closeButton = event.target.closest('[data-card-inline-quick-add-close]');
    if (closeButton && ownsElement(closeButton)) {
      event.preventDefault();
      this.#closeInlineQuickAdd();
      return;
    }

    const overlay = event.target.closest('[data-card-inline-quick-add-overlay]');
    if (overlay && ownsElement(overlay)) {
      event.preventDefault();
      this.#closeInlineQuickAdd();
    }
  };

  /**
   * Applies the selected stitcher product content to the current card.
   * @param {{ brand?: string, title?: string, categoryLabel?: string, url?: string, priceHtml?: string, imageUrl?: string, hoverImageUrl?: string, featuredMediaUrl?: string, imageAlt?: string, quickAdd?: { productId?: string, brand?: string, title?: string, priceHtml?: string, summaryImageUrl?: string, displayLabel?: string, sizeLabel?: string, selectedVariantId?: string, sizes?: Array<{ id?: string, label?: string, available?: boolean, url?: string, imageUrl?: string, displayLabel?: string }> } }} stitcherProduct
   */
  #applyStitcherProduct(stitcherProduct) {
    const { productBrand, productCategoryLabel, productPrice, productCardLink, productTitleLink, cardGalleryLink } =
      this.refs;

    if (productBrand instanceof HTMLElement) {
      const brand = typeof stitcherProduct.brand === 'string' ? stitcherProduct.brand.trim() : '';
      productBrand.textContent = brand;
      productBrand.hidden = brand.length === 0;
    }

    if (productCategoryLabel instanceof HTMLElement) {
      const categoryLabel =
        typeof stitcherProduct.categoryLabel === 'string' ? stitcherProduct.categoryLabel.trim() : '';
      productCategoryLabel.textContent = categoryLabel;
      productCategoryLabel.hidden = categoryLabel.length === 0;
    }

    if (productPrice instanceof HTMLElement && typeof stitcherProduct.priceHtml === 'string') {
      productPrice.innerHTML = stitcherProduct.priceHtml;
    }

    if (productTitleLink instanceof HTMLAnchorElement && typeof stitcherProduct.title === 'string') {
      productTitleLink.textContent = stitcherProduct.title;
    }

    if (typeof stitcherProduct.url === 'string' && stitcherProduct.url.length > 0) {
      productCardLink.href = stitcherProduct.url;

      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = stitcherProduct.url;
      }

      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = stitcherProduct.url;
      }
    }

    if (typeof stitcherProduct.title === 'string') {
      const accessibleTitle = productCardLink.querySelector('.visually-hidden');
      if (accessibleTitle instanceof HTMLElement) {
        accessibleTitle.textContent = stitcherProduct.title;
      }
    }

    if (typeof stitcherProduct.featuredMediaUrl === 'string' && stitcherProduct.featuredMediaUrl.length > 0) {
      this.setAttribute('data-featured-media-url', stitcherProduct.featuredMediaUrl);
    }

    if (typeof stitcherProduct.imageUrl === 'string' && stitcherProduct.imageUrl.length > 0) {
      const images = Array.from(this.querySelectorAll('.card-gallery .product-media__image'));
      const primaryImage = images[0];
      const hoverImage = images[1];

      this.#updateCardImage(primaryImage, stitcherProduct.imageUrl, stitcherProduct.imageAlt);

      if (hoverImage instanceof HTMLImageElement) {
        const hoverImageUrl =
          typeof stitcherProduct.hoverImageUrl === 'string' && stitcherProduct.hoverImageUrl.length > 0
            ? stitcherProduct.hoverImageUrl
            : stitcherProduct.imageUrl;

        this.#updateCardImage(hoverImage, hoverImageUrl, stitcherProduct.imageAlt);
      }
    }

    if (stitcherProduct.quickAdd) {
      this.#renderInlineQuickAdd(stitcherProduct.quickAdd);
    }
  }

  /**
   * Updates a single product card image node.
   * @param {HTMLImageElement | undefined} image
   * @param {string} imageUrl
   * @param {string | undefined} imageAlt
   */
  #updateCardImage(image, imageUrl, imageAlt) {
    if (!(image instanceof HTMLImageElement)) return;

    image.src = imageUrl;
    image.srcset = imageUrl;
    image.dataset.maxResolution = imageUrl;

    if (typeof imageAlt === 'string') {
      image.alt = imageAlt;
    }
  }

  /**
   * Returns the inline quick add panel, even when it has been moved to the body.
   * @returns {HTMLElement | null}
   */
  #getInlineQuickAddPanel() {
    if (this.#inlineQuickAddPanel) return this.#inlineQuickAddPanel;

    const quickAddPanel = this.querySelector('[data-card-inline-quick-add]');
    if (quickAddPanel instanceof HTMLElement) {
      this.#inlineQuickAddPanel = quickAddPanel;
    }

    return this.#inlineQuickAddPanel;
  }

  /**
   * Moves the panel to the body on mobile so position:fixed is not trapped
   * by transformed ancestors (sliders, scroll animations).
   * @param {HTMLElement} quickAddPanel
   */
  #portalInlineQuickAdd(quickAddPanel) {
    if (quickAddPanel.parentElement === document.body) return;

    if (!this.#inlineQuickAddPlaceholder) {
      this.#inlineQuickAddPlaceholder = document.createComment('card-inline-quick-add');
    }

    quickAddPanel.replaceWith(this.#inlineQuickAddPlaceholder);
    document.body.appendChild(quickAddPanel);
    quickAddPanel.addEventListener('click', this.handleInlineQuickAddClick);
    quickAddPanel.addEventListener('click', this.#handleStitcherClick);
  }

  /**
   * Moves the panel back inside the product card.
   * @param {HTMLElement} quickAddPanel
   */
  #restoreInlineQuickAdd(quickAddPanel) {
    if (quickAddPanel.parentElement !== document.body) return;

    quickAddPanel.removeEventListener('click', this.handleInlineQuickAddClick);
    quickAddPanel.removeEventListener('click', this.#handleStitcherClick);

    if (this.#inlineQuickAddPlaceholder?.parentNode) {
      this.#inlineQuickAddPlaceholder.replaceWith(quickAddPanel);
    } else {
      quickAddPanel.remove();
    }
  }

  /**
   * Directly submits the add-to-cart form without opening the quick add modal.
   */
  #directAddToCart() {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    const submitButton = quickAddPanel?.querySelector('[ref="addToCartButton"]');
    const quickViewButton = this.querySelector('.card_quickview_new');

    if (!(submitButton instanceof HTMLButtonElement) || submitButton.disabled) return;
    if (!(quickViewButton instanceof HTMLElement)) return;

    quickViewButton.setAttribute('data-loading', '');

    const productForm = quickAddPanel?.querySelector('product-form-component');
    const clearLoading = () => quickViewButton.removeAttribute('data-loading');
    const onSuccess = () => {
      clearLoading();
      quickViewButton.setAttribute('data-added', '');
      setTimeout(() => quickViewButton.removeAttribute('data-added'), 1500);
    };

    if (productForm) {
      const cleanup = () => {
        productForm.removeEventListener('cart:update', handleSuccess);
        productForm.removeEventListener('cart:error', handleError);
      };
      const handleSuccess = () => { cleanup(); onSuccess(); };
      const handleError = () => { cleanup(); clearLoading(); };
      productForm.addEventListener('cart:update', handleSuccess);
      productForm.addEventListener('cart:error', handleError);
    }

    submitButton.click();
  }

  /**
   * Toggles the inline quick add panel inside the product card.
   */
  #toggleInlineQuickAdd() {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    if (!(quickAddPanel instanceof HTMLElement)) return;

    const shouldOpen = quickAddPanel.hasAttribute('hidden');

    if (shouldOpen && !isDesktopBreakpoint()) {
      this.#portalInlineQuickAdd(quickAddPanel);
      document.body.classList.add('quickadd_modal_open_new');
    }

    quickAddPanel.toggleAttribute('hidden', !shouldOpen);
    this.toggleAttribute('data-inline-quick-add-open', shouldOpen);

    if (shouldOpen) {
      const activeButton = quickAddPanel.querySelector('[data-quick-size-option].active');
      if (activeButton instanceof HTMLButtonElement) {
        this.#selectInlineQuickAddSize(activeButton, { preserveOpenState: true });
      }
      requestAnimationFrame(() => this.#updateSizeNavVisibility());
    } else {
      this.#restoreInlineQuickAdd(quickAddPanel);
      document.body.classList.remove('quickadd_modal_open_new');
    }
  }

  /**
   * Closes the inline quick add panel.
   */
  #closeInlineQuickAdd() {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    if (!(quickAddPanel instanceof HTMLElement) || quickAddPanel.hasAttribute('hidden')) return;

    quickAddPanel.setAttribute('hidden', '');
    this.removeAttribute('data-inline-quick-add-open');
    this.#restoreInlineQuickAdd(quickAddPanel);
    document.body.classList.remove('quickadd_modal_open_new');
  }

  /**
   * Closes the inline quick add when clicking outside the panel.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const quickAddPanel = this.#getInlineQuickAddPanel();
    if (!(quickAddPanel instanceof HTMLElement) || quickAddPanel.hasAttribute('hidden')) return;
    if (quickAddPanel.contains(event.target)) return;
    if (event.target.closest('.card_quickview_new') && this.contains(event.target)) return;

    this.#closeInlineQuickAdd();
  };

  /**
   * Scrolls the inline size list.
   * @param {number} direction
   */
  #scrollInlineQuickAddSizes(direction) {
    const sizesContainer = this.#getInlineQuickAddPanel()?.querySelector('[data-quick-size-options]');
    if (!(sizesContainer instanceof HTMLElement)) return;

    sizesContainer.scrollBy({
      left: direction * 180,
      behavior: 'smooth',
    });
  }

  /**
   * Shows or hides the size nav buttons based on whether the sizes container overflows.
   */
  #updateSizeNavVisibility() {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    if (!quickAddPanel) return;

    const sizesContainer = quickAddPanel.querySelector('[data-quick-size-options]');
    const navButtons = quickAddPanel.querySelectorAll('[data-quick-size-nav]');
    if (!(sizesContainer instanceof HTMLElement) || navButtons.length === 0) return;

    const isOverflowing = sizesContainer.scrollWidth > sizesContainer.clientWidth;
    for (const btn of navButtons) {
      if (btn instanceof HTMLElement) {
        btn.hidden = !isOverflowing;
      }
    }
  }

  /**
   * Renders inline quick add sizes for the selected stitcher product.
   * @param {{ productId?: string, brand?: string, title?: string, priceHtml?: string, summaryImageUrl?: string, displayLabel?: string, sizeLabel?: string, selectedVariantId?: string, sizes?: Array<{ id?: string, label?: string, available?: boolean, url?: string, imageUrl?: string, displayLabel?: string }> }} quickAdd
   */
  #renderInlineQuickAdd(quickAdd) {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    const sizeLabel = quickAddPanel?.querySelector('[data-quick-size-label]');
    const sizesContainer = quickAddPanel?.querySelector('[data-quick-size-options]');
    const productForm = quickAddPanel?.querySelector('product-form-component');
    const modalBrand = quickAddPanel?.querySelector('[ref="modalProductBrand"]');
    const modalTitle = quickAddPanel?.querySelector('[ref="modalProductTitle"]');
    const modalPrice = quickAddPanel?.querySelector('[ref="modalProductPrice"]');
    const modalImage = quickAddPanel?.querySelector('.card_inline_quickadd_summary_image_new img');

    if (!(quickAddPanel instanceof HTMLElement) || !(sizesContainer instanceof HTMLElement)) return;

    if (sizeLabel instanceof HTMLElement) {
      sizeLabel.textContent = quickAdd.sizeLabel || 'Size';
    }

    if (productForm instanceof HTMLElement && quickAdd.productId) {
      productForm.dataset.productId = quickAdd.productId.toString();
    }

    if (modalBrand instanceof HTMLElement) {
      modalBrand.textContent = quickAdd.brand || '';
    }

    if (modalTitle instanceof HTMLElement) {
      modalTitle.textContent = quickAdd.title || '';
    }

    if (modalPrice instanceof HTMLElement && typeof quickAdd.priceHtml === 'string') {
      modalPrice.innerHTML = quickAdd.priceHtml;
    }

    if (modalImage instanceof HTMLImageElement && typeof quickAdd.summaryImageUrl === 'string' && quickAdd.summaryImageUrl.length > 0) {
      // The summary image is a <picture>; its <source> srcsets take precedence
      // over the <img>, so they must be updated as well.
      const pictureSources = modalImage.closest('picture')?.querySelectorAll('source') || [];
      for (const source of pictureSources) {
        source.srcset = quickAdd.summaryImageUrl;
      }

      modalImage.src = quickAdd.summaryImageUrl;
      modalImage.srcset = quickAdd.summaryImageUrl;
      modalImage.alt = quickAdd.title || modalImage.alt;
    }

    const fragment = document.createDocumentFragment();
    const sizes = Array.isArray(quickAdd.sizes) ? quickAdd.sizes : [];
    const selectedSize =
      sizes.find((size) => String(size.id || '') === String(quickAdd.selectedVariantId || '')) ||
      sizes.find((size) => size.available) ||
      sizes[0];

    for (const size of sizes) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'card_inline_quickadd_size_new';
      button.dataset.quickSizeOption = '';
      button.dataset.sizeLabel = size.label || '';
      button.dataset.variantId = String(size.id || '');
      button.dataset.variantAvailable = size.available ? 'true' : 'false';

      if (size.url) {
        button.dataset.variantUrl = size.url;
      }

      if (size.imageUrl) {
        button.dataset.variantImageUrl = size.imageUrl;
      }

      if (size.displayLabel) {
        button.dataset.displayLabel = size.displayLabel;
      }

      button.textContent = size.label || '';

      if (!size.available) {
        button.disabled = true;
      }

      if (selectedSize && String(size.id || '') === String(selectedSize.id || '')) {
        button.classList.add('active');
      }

      fragment.appendChild(button);
    }

    sizesContainer.replaceChildren(fragment);

    const activeButton = sizesContainer.querySelector('.card_inline_quickadd_size_new.active');
    if (activeButton instanceof HTMLButtonElement) {
      this.#selectInlineQuickAddSize(activeButton, { preserveOpenState: true });
    }

    requestAnimationFrame(() => this.#updateSizeNavVisibility());
  }

  /**
   * Selects a size inside the inline quick add panel.
   * @param {HTMLButtonElement} sizeButton
   * @param {{ preserveOpenState?: boolean }} [options]
   */
  #selectInlineQuickAddSize(sizeButton, options = {}) {
    const quickAddPanel = this.#getInlineQuickAddPanel();
    const productForm = quickAddPanel?.querySelector('product-form-component');
    const variantIdInput = productForm?.querySelector('input[name="id"]');
    const addToCartContainer = productForm?.querySelector('add-to-cart-component');
    const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
    const addToCartText = addToCartButton?.querySelector('.add-to-cart-text__content span span');
    const sizeButtons = quickAddPanel?.querySelectorAll('[data-quick-size-option]');

    if (!(quickAddPanel instanceof HTMLElement) || !(variantIdInput instanceof HTMLInputElement)) return;

    for (const button of sizeButtons || []) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.classList.toggle('active', button === sizeButton);
    }

    variantIdInput.value = sizeButton.dataset.variantId || '';

    const isAvailable = sizeButton.dataset.variantAvailable === 'true';
    if (addToCartContainer && 'enable' in addToCartContainer && 'disable' in addToCartContainer) {
      if (isAvailable) {
        addToCartContainer.enable();
      } else {
        addToCartContainer.disable();
      }
    } else if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !isAvailable;
    }

    if (typeof sizeButton.dataset.variantImageUrl === 'string' && sizeButton.dataset.variantImageUrl.length > 0) {
      addToCartContainer?.setAttribute('data-product-variant-media', sizeButton.dataset.variantImageUrl);
    }

    if (
      typeof sizeButton.dataset.variantUrl === 'string' &&
      sizeButton.dataset.variantUrl.length > 0 &&
      this.#panelMatchesCardProduct()
    ) {
      const variantUrl = sizeButton.dataset.variantUrl;
      this.refs.productCardLink.href = variantUrl;

      if (this.refs.productTitleLink instanceof HTMLAnchorElement) {
        this.refs.productTitleLink.href = variantUrl;
      }

      if (this.refs.cardGalleryLink instanceof HTMLAnchorElement) {
        this.refs.cardGalleryLink.href = variantUrl;
      }
    }

    if (addToCartText instanceof HTMLElement) {
      if (isDesktopBreakpoint()) {
        addToCartText.textContent = 'ADD TO BAG';
      } else {
        const sizeLabel = sizeButton.dataset.sizeLabel || sizeButton.textContent?.trim() || '';
        const displayLabel = sizeButton.dataset.displayLabel || this.#getActiveStitcherDisplayLabel();
        const ctaParts = ['ADD TO BAG'];
        if (sizeLabel) ctaParts.push(sizeLabel);
        if (displayLabel) ctaParts.push(displayLabel);
        addToCartText.textContent = ctaParts.join(' - ');
      }
    }

    if (!options.preserveOpenState) {
      quickAddPanel.removeAttribute('hidden');
      this.setAttribute('data-inline-quick-add-open', 'true');
    }
  }

  /**
   * Returns true when the quick add panel shows the same product as the card,
   * so card links are only synced for the card's own product.
   * @returns {boolean}
   */
  #panelMatchesCardProduct() {
    const cardSwatch = this.querySelector('.card_swatch_item_new.active');
    const panelForm = this.#getInlineQuickAddPanel()?.querySelector('product-form-component');

    if (!(cardSwatch instanceof HTMLElement) || !(panelForm instanceof HTMLElement)) return true;

    const cardProductId = cardSwatch.dataset.productId;
    const panelProductId = panelForm.dataset.productId;

    if (!cardProductId || !panelProductId) return true;

    return cardProductId === panelProductId;
  }

  /**
   * Returns the label for the currently active stitcher in the mobile modal.
   * @returns {string}
   */
  #getActiveStitcherDisplayLabel() {
    const activeStitcher = this.#getInlineQuickAddPanel()?.querySelector(
      '.card_inline_quickadd_stitcher_new.active .card_inline_quickadd_stitcher_label_new'
    );
    if (activeStitcher instanceof HTMLElement) {
      return activeStitcher.textContent?.trim() || '';
    }

    return '';
  }

  /**
   * Updates the active state for the stitcher buttons.
   * @param {HTMLButtonElement} activeButton
   * @param {{ panelOnly?: boolean }} [options] - panelOnly restricts the update to the quick add panel buttons.
   */
  #updateActiveStitcher(activeButton, options = {}) {
    const panelButtons = this.#getInlineQuickAddPanel()?.querySelectorAll('[data-stitcher-button]') || [];
    const stitcherButtons = options.panelOnly
      ? [...panelButtons]
      : [...this.querySelectorAll('[data-stitcher-button]'), ...panelButtons];
    const activeProductId = activeButton.dataset.productId;

    for (const button of stitcherButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const isActive = button.dataset.productId === activeProductId;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
